import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Clock, Trophy, RefreshCw, MessageCircle, Megaphone, History } from 'lucide-react';
import axios from 'axios';
import Logo from './Logo';

// Cloudflare Tunnel doesn't require special headers

function App() {
  const [arbitrageData, setArbitrageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [cachedScanTimestamp, setCachedScanTimestamp] = useState(null); // Cache previous scanTimestamp to detect changes
  const [connectionStatus, setConnectionStatus] = useState('连接中...');
  const cachedListRef = useRef(null); // Cache the rendered list to prevent recomputation when nothing changed
  const shouldSkipRenderRef = useRef(false); // Flag to skip renderArbitrageList when no changes
  const arbitrageSectionRef = useRef(null); // Ref for arbitrage section container
  const arbitrageListRef = useRef(null); // Ref for arbitrage list container
  const historyContainerRef = useRef(null); // Ref for history container
  const [historyMaxHeight, setHistoryMaxHeight] = useState(null); // Dynamic height for history container
  const [arbitrageListHeight, setArbitrageListHeight] = useState(null); // Fixed height for arbitrage list (5 items)
  const [viewerCount, setViewerCount] = useState(0);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [nextRefresh, setNextRefresh] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(30 * 60 * 1000); // 30 minutes default
  const [countdown, setCountdown] = useState(0);
  const [isScanning, setIsScanning] = useState(false);
  const [serverCountdownState, setServerCountdownState] = useState(null);
  const [apiUrlStatus, setApiUrlStatus] = useState('checking');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [manualUrl, setManualUrl] = useState('');
  const [apiUrlError, setApiUrlError] = useState(null);
  const formatBrisbaneTime = (date) => {
    try {
      return new Intl.DateTimeFormat('zh-CN', {
        timeZone: 'Australia/Brisbane',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }).format(date);
    } catch (_) {
      return date.toLocaleTimeString();
    }
  };

  // Format event time to Brisbane time
  const formatEventTime = (eventString) => {
    if (!eventString) return eventString;
    
    // Parse event string format: "2024-01-15T20:00:00Z | Sport | Event Name"
    const parts = eventString.split('|');
    if (parts.length < 3) return eventString;
    
    const timePart = parts[0].trim();
    try {
      const eventDate = new Date(timePart);
      if (isNaN(eventDate.getTime())) return eventString;
      
      // Format to Brisbane time with date and time
      const brisbaneTime = new Intl.DateTimeFormat('zh-CN', {
        timeZone: 'Australia/Brisbane',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(eventDate);
      
      // Return formatted string: "2024-01-16 06:00 (Brisbane) | Sport | Event Name"
      return `${brisbaneTime} (Brisbane) | ${parts.slice(1).join('|').trim()}`;
    } catch (_) {
      return eventString;
    }
  };

  // Get API base URL at runtime with dynamic Cloudflare Tunnel detection
  const getApiBaseUrl = () => {
    // List of invalid URLs that should be rejected
    const invalidUrls = [
      'https://www.cloudflare.com',
      'http://www.cloudflare.com',
      'https://cloudflare.com',
      'http://cloudflare.com',
      'https://your-tunnel-url.trycloudflare.com',
      'http://your-tunnel-url.trycloudflare.com',
      'null',
      'undefined',
      ''
    ];
    
    // Validate URL format
    const isValidTunnelUrl = (url) => {
      if (!url || typeof url !== 'string') return false;
      if (invalidUrls.includes(url.toLowerCase())) return false;
      // Must be a valid URL
      try {
        const urlObj = new URL(url);
        // Must be https
        if (urlObj.protocol !== 'https:') return false;
        // Must be trycloudflare.com or a custom domain
        if (!urlObj.hostname.includes('trycloudflare.com') && !urlObj.hostname.includes('cloudflaretunnel.com')) {
          // Allow custom domains but reject obvious invalid ones
          if (urlObj.hostname === 'www.cloudflare.com' || urlObj.hostname === 'cloudflare.com') {
            return false;
          }
        }
        return true;
      } catch (e) {
        return false;
      }
    };
    
    // Try to get Cloudflare Tunnel URL from environment variable first (build-time, most reliable)
    if (process.env.REACT_APP_API_URL && isValidTunnelUrl(process.env.REACT_APP_API_URL)) {
      return process.env.REACT_APP_API_URL;
    }
    
    // Try to get Cloudflare Tunnel URL from localStorage (updated dynamically)
    const storedUrl = localStorage.getItem('cloudflare_api_url') || localStorage.getItem('ngrok_api_url'); // Support both for migration
    if (storedUrl && isValidTunnelUrl(storedUrl)) {
      return storedUrl;
    }
    
    // No valid API URL found - throw error
    throw new Error('API_URL_NOT_DEFINED');
  };

  // Initialize API_BASE_URL with error handling
  const [API_BASE_URL, setAPI_BASE_URL] = useState(() => {
    try {
      return getApiBaseUrl();
    } catch (error) {
      setApiUrlError('API URL not defined. Please start the local services and update the URL.');
      return null;
    }
  });
  
  // Check if Ruby monitoring is enabled (disable React countdown)
  const RUBY_MONITORING = process.env.REACT_APP_RUBY_MONITORING === 'true';
  
  // Countdown handled by server-state; show when nextRefresh is present

  // Load configuration
  const [config, setConfig] = useState({
    refreshIntervals: {
      low: 30 * 60 * 1000,    // 30 minutes for < 10 viewers
      medium: 15 * 60 * 1000, // 15 minutes for 10-100 viewers
      high: 5 * 60 * 1000     // 5 minutes for > 100 viewers
    },
    viewerThresholds: {
      low: 10,
      medium: 100
    }
  });

  useEffect(() => {
    // Only proceed if API_BASE_URL is defined
    if (!API_BASE_URL) {
      setApiUrlStatus('error');
      return;
    }

    // Initial one-time loads
    loadConfig();
    fetchArbitrageData();
    updateViewerCount();
    fetchCountdownState();
  }, [API_BASE_URL]);

  // Polling: continue when we have results OR when we have a scanTimestamp (lastUpdate)
  // This ensures 最后更新 stays updated even when items array is empty
  useEffect(() => {
    if (!API_BASE_URL) return;
    
    // Poll if we have data OR if we have a lastUpdate (scanTimestamp exists)
    if (!arbitrageData || (arbitrageData.length === 0 && !lastUpdate)) {
      return; // Only stop polling if both data and lastUpdate are empty
    }

    const interval = setInterval(() => {
      fetchArbitrageData(true); // quiet mode: don't toggle loading/connection or clear state
      // Only fetch countdown and viewer count - they don't affect scanTimestamp-based updates
      fetchCountdownState();
      updateViewerCount();
    }, 5000);

    // Set up a separate interval for history refresh (less frequent, every 30 seconds)
    const historyInterval = setInterval(() => {
      fetchHistoryData();
    }, 30000); // Refresh history every 30 seconds

    return () => {
      clearInterval(interval);
      clearInterval(historyInterval);
    };
  }, [API_BASE_URL, arbitrageData, lastUpdate]);

  // Refresh history on initial load and when scanTimestamp changes
  useEffect(() => {
    if (!API_BASE_URL) return;
    // Fetch history on initial load or when scanTimestamp changes
    fetchHistoryData();
  }, [API_BASE_URL, cachedScanTimestamp]);

  // Calculate fixed height for arbitrage list (5 items) and sync history height
  useEffect(() => {
    if (!arbitrageListRef.current || !historyContainerRef.current) {
      return;
    }

    let isUpdating = false;
    let lastArbitrageHeight = 0;
    let lastHistoryHeight = 0;

    const updateHeights = () => {
      if (isUpdating) return;
      
      const arbitrageListElement = arbitrageListRef.current;
      const historyElement = historyContainerRef.current;
      
      if (!arbitrageListElement || !historyElement) return;
      
      const sidebarCard = historyElement.closest('.sidebar-card');
      if (!sidebarCard) return;
      
      // Calculate height for 5 arbitrage cards
      // Get the first arbitrage card to measure its height
      const firstCard = arbitrageListElement.querySelector('.arbitrage-card');
      if (firstCard) {
        const cardHeight = firstCard.offsetHeight;
        const cardMarginBottom = 15; // margin-bottom from CSS
        const heightFor5Items = (cardHeight + cardMarginBottom) * 5 - cardMarginBottom; // 5 cards with margins
        
        // Only update if height changed significantly
        if (Math.abs(heightFor5Items - lastArbitrageHeight) > 1) {
          isUpdating = true;
          lastArbitrageHeight = heightFor5Items;
          setArbitrageListHeight(heightFor5Items);
          
          // Sync history height to match arbitrage list height
          const historyTitle = sidebarCard.querySelector('.section-title');
          const titleHeight = historyTitle?.offsetHeight || 0;
          const padding = 40; // 20px top + 20px bottom
          
          // History container should match arbitrage list height
          const historyHeight = heightFor5Items;
          const newHistoryHeight = Math.max(0, historyHeight);
          
          if (Math.abs(newHistoryHeight - lastHistoryHeight) > 1) {
            lastHistoryHeight = newHistoryHeight;
            setHistoryMaxHeight(newHistoryHeight);
          }
          
          requestAnimationFrame(() => {
            isUpdating = false;
          });
        }
      } else if (arbitrageData.length === 0) {
        // No cards, use a default height based on empty state
        const defaultHeight = 200; // Default height for empty state
        if (Math.abs(defaultHeight - lastArbitrageHeight) > 1) {
          isUpdating = true;
          lastArbitrageHeight = defaultHeight;
          setArbitrageListHeight(defaultHeight);
          setHistoryMaxHeight(defaultHeight);
          requestAnimationFrame(() => {
            isUpdating = false;
          });
        }
      }
    };

    // Initial calculation
    const initialTimeout = setTimeout(() => {
      updateHeights();
    }, 200);

    // Use ResizeObserver to watch for changes in arbitrage cards
    const resizeObserver = new ResizeObserver((entries) => {
      clearTimeout(resizeObserver.timeoutId);
      resizeObserver.timeoutId = setTimeout(() => {
        updateHeights();
      }, 100);
    });

    if (arbitrageListRef.current) {
      resizeObserver.observe(arbitrageListRef.current);
      // Also observe individual cards
      const cards = arbitrageListRef.current.querySelectorAll('.arbitrage-card');
      cards.forEach(card => resizeObserver.observe(card));
    }

    // Update when data changes
    const dataTimeout = setTimeout(() => {
      updateHeights();
    }, 300);

    return () => {
      resizeObserver.disconnect();
      if (resizeObserver.timeoutId) {
        clearTimeout(resizeObserver.timeoutId);
      }
      clearTimeout(initialTimeout);
      clearTimeout(dataTimeout);
      isUpdating = false;
    };
  }, [arbitrageData, historyData, loading]);

  // Function to detect and update Cloudflare Tunnel URL
  // Note: Cloudflare Tunnel doesn't have a local API like ngrok
  // The URL is shown in the console output and passed via environment variable or localStorage
  const detectAndUpdateCloudflareUrl = async () => {
    // Cloudflare Tunnel doesn't expose a local API for URL detection
    // The URL must be set via environment variable or manually in localStorage
    console.log('⚠️ Cloudflare Tunnel URL detection not available via API');
    console.log('💡 Set the URL manually in browser console: localStorage.setItem("cloudflare_api_url", "https://your-url.trycloudflare.com")');
    return null;
  };

  // Manual URL update function
  const updateApiUrl = (newUrl) => {
    if (!newUrl || typeof newUrl !== 'string') {
      console.error('❌ Invalid URL: URL is required');
      return;
    }
    
    // Validate URL using the same logic as getApiBaseUrl
    const invalidUrls = [
      'https://www.cloudflare.com',
      'http://www.cloudflare.com',
      'https://cloudflare.com',
      'http://cloudflare.com',
      'https://your-tunnel-url.trycloudflare.com',
      'http://your-tunnel-url.trycloudflare.com',
      'null',
      'undefined',
      ''
    ];
    
    const isValidTunnelUrl = (url) => {
      if (!url || typeof url !== 'string') return false;
      if (invalidUrls.includes(url.toLowerCase())) return false;
      try {
        const urlObj = new URL(url);
        if (urlObj.protocol !== 'https:') return false;
        if (!urlObj.hostname.includes('trycloudflare.com') && !urlObj.hostname.includes('cloudflaretunnel.com')) {
          if (urlObj.hostname === 'www.cloudflare.com' || urlObj.hostname === 'cloudflare.com') {
            return false;
          }
        }
        return true;
      } catch (e) {
        return false;
      }
    };
    
    if (!isValidTunnelUrl(newUrl)) {
      console.error('❌ Invalid URL format:', newUrl);
      setApiUrlError(`Invalid URL format. Please use a valid Cloudflare Tunnel URL (e.g., https://xxxx-xx-xx-xx-xx.trycloudflare.com)`);
      return;
    }
    
    localStorage.setItem('cloudflare_api_url', newUrl);
    // Also keep ngrok_api_url for backward compatibility during migration
    localStorage.setItem('ngrok_api_url', newUrl);
    setAPI_BASE_URL(newUrl);
    setApiUrlStatus('checking');
    setApiUrlError(null);
    setShowUrlInput(false);
    setManualUrl('');
    console.log('✅ Manually updated API URL to:', newUrl);
    // Retry loading config with new URL
    loadConfig();
  };

  const loadConfig = async () => {
    if (!API_BASE_URL) {
      setApiUrlStatus('error');
      return;
    }
    
    // Check if URL is invalid (like www.cloudflare.com)
    if (API_BASE_URL.includes('www.cloudflare.com') || API_BASE_URL === 'https://cloudflare.com') {
      console.error('❌ Invalid API URL detected:', API_BASE_URL);
      setApiUrlError(`Invalid API URL: ${API_BASE_URL}. Please clear and set a valid Cloudflare Tunnel URL.`);
      setApiUrlStatus('error');
      console.error('💡 To fix this, run in browser console:');
      console.error('   localStorage.removeItem("cloudflare_api_url");');
      console.error('   localStorage.removeItem("ngrok_api_url");');
      console.error('   Then set the correct URL: localStorage.setItem("cloudflare_api_url", "https://xxxx-xx-xx-xx-xx.trycloudflare.com")');
      return;
    }
    
    try {
      console.log('🌐 Attempting to load config from:', API_BASE_URL);
      setApiUrlStatus('checking');
      const response = await axios.get(`${API_BASE_URL}/api/config`, {
        timeout: 10000
      });
      setConfig(response.data);
      setApiUrlStatus('connected');
      setApiUrlError(null);
      console.log('✅ Config loaded successfully');
    } catch (error) {
      console.error('❌ Error loading config:', error);
      console.error('🔗 API URL:', API_BASE_URL);
      setApiUrlStatus('error');
      
      if (error.code === 'ERR_NETWORK' || error.message.includes('ERR_CONNECTION_REFUSED') || error.message.includes('ERR_FAILED')) {
        console.error('💡 API connection failed. Possible issues:');
        console.error('   1. Invalid API URL stored in localStorage');
        console.error('   2. Cloudflare Tunnel is not running');
        console.error('   3. CORS configuration issue');
        console.error('💡 To fix:');
        console.error('   - Clear localStorage: localStorage.removeItem("cloudflare_api_url")');
        console.error('   - Get the correct URL from: cat logs/tunnel_url.txt');
        console.error('   - Set it: localStorage.setItem("cloudflare_api_url", "https://xxxx-xx-xx-xx-xx.trycloudflare.com")');
        setApiUrlError(`Connection failed. Please check if the Cloudflare Tunnel URL is correct. Current URL: ${API_BASE_URL}`);
      }
    }
  };

  const fetchCountdownState = async () => {
    if (!API_BASE_URL) return;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/api/countdown`);
      const serverState = response.data;
      setServerCountdownState(serverState);
      
      // Update local state from server
      let stateUpdated = false;
      if (serverState.nextRefresh) {
        const next = new Date(serverState.nextRefresh);
        setNextRefresh(next);
        stateUpdated = true;
        // Guard: if nextRefresh is in the past, ensure scanning is false
        if (next.getTime() <= Date.now()) {
          setIsScanning(false);
        }
      }
      if (serverState.refreshInterval) {
        setRefreshInterval(serverState.refreshInterval);
        stateUpdated = true;
      }
      if (serverState.isScanning !== undefined) {
        setIsScanning(serverState.isScanning);
        stateUpdated = true;
      }
      
      if (stateUpdated) {
        console.log('🔄 Countdown state updated (separate from scanTimestamp)');
      }
      // lastUpdate should ONLY come from scanTimestamp via /api/arbitrage, not from countdown state
    } catch (error) {
      console.error('Error fetching countdown state:', error);
    }
  };

  // Remove this useEffect since we now get nextRefresh from server

  // Countdown timer effect (only if Ruby monitoring is disabled)
  useEffect(() => {
    if (RUBY_MONITORING || !nextRefresh) return;

    const updateCountdown = () => {
      const now = Date.now();
      const timeLeft = nextRefresh.getTime() - now;
      
      if (timeLeft <= 0) {
        setCountdown(0);
        // Guard: when cooldown ends, clear scanning state
        if (isScanning) {
          setIsScanning(false);
        }
        return;
      }
      
      setCountdown(timeLeft);
    };

    // Update immediately
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [nextRefresh, refreshInterval, RUBY_MONITORING, isScanning]);

  const updateViewerCount = async () => {
    if (!API_BASE_URL) return;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/api/viewers`);
      const count = response.data.count || 0;
      const viewerCountChanged = count !== viewerCount;
      setViewerCount(count);
      
      // Update refresh interval based on viewer count
      let newInterval;
      if (count < config.viewerThresholds.low) {
        newInterval = config.refreshIntervals.low;
      } else if (count <= config.viewerThresholds.medium) {
        newInterval = config.refreshIntervals.medium;
      } else {
        newInterval = config.refreshIntervals.high;
      }
      
      const intervalChanged = newInterval !== refreshInterval;
      if (intervalChanged) {
        setRefreshInterval(newInterval);
      }
      
      if (viewerCountChanged || intervalChanged) {
        console.log('👥 Viewer/interval state updated (separate from scanTimestamp)', {
          viewerCountChanged,
          intervalChanged
        });
      }
    } catch (error) {
      console.error('Error updating viewer count:', error);
      // Default to low interval if API fails
      if (refreshInterval !== config.refreshIntervals.low) {
        setRefreshInterval(config.refreshIntervals.low);
      }
    }
  };

  const fetchArbitrageData = async (quiet = false) => {
    if (!API_BASE_URL) {
      if (!quiet) setLoading(false);
      setConnectionStatus('API URL 未定义');
      setArbitrageData([]);
      return;
    }
    
    try {
      if (!quiet) {
        setLoading(true);
        setConnectionStatus('连接中...');
      }
      
      console.log('🔄 Fetching arbitrage data from:', API_BASE_URL);
      const response = await axios.get(`${API_BASE_URL}/api/arbitrage`);
      let apiPayload = response.data;
      let data = apiPayload;
      let apiLastUpdate = null;
      console.log('🔍 API Response raw:', data);
      
      // API now returns { data, lastUpdate, count } where lastUpdate is scanTimestamp
      if (apiPayload && typeof apiPayload === 'object' && 'lastUpdate' in apiPayload) {
        apiLastUpdate = apiPayload.lastUpdate;
        data = apiPayload.data || apiPayload.items || apiPayload;
      }
      
      // Attempt to parse if response is a JSON string
      if (typeof data === 'string') {
        try {
          const parsed = JSON.parse(data);
          data = parsed;
          console.log('🔍 Parsed string response into JSON');
        } catch (e) {
          console.warn('⚠️ Response was string and not valid JSON');
        }
      }
      
      // Support common wrappers and capture API-provided lastUpdate
      if (!Array.isArray(data) && data && Array.isArray(data.items)) {
        data = data.items;
        if (!apiLastUpdate) {
          apiLastUpdate = apiPayload.lastUpdate || null;
        }
      }
      if (!Array.isArray(data) && data && Array.isArray(data.data)) {
        if (!apiLastUpdate) {
          apiLastUpdate = apiPayload.lastUpdate || null;
        }
        data = data.data;
      }
      
      // Only update data if we have an array
      if (data && Array.isArray(data)) {
        // Check if scanTimestamp has changed
        const prevScanTimestamp = cachedScanTimestamp;
        const currentScanTimestamp = apiLastUpdate;
        const scanTimestampChanged = currentScanTimestamp !== prevScanTimestamp;
        
        // Log comparison details
        console.log('🔍 scanTimestamp Comparison:', {
          previous: prevScanTimestamp || '(null)',
          current: currentScanTimestamp || '(null)',
          changed: scanTimestampChanged ? '✅ YES - UI will refresh' : '❌ NO - UI unchanged',
          comparison: prevScanTimestamp === currentScanTimestamp ? 'equal' : 'different'
        });
        
        if (scanTimestampChanged) {
          console.log('🔄 scanTimestamp changed - updating UI elements');
          
          // Update UI elements only when scanTimestamp changes
          setArbitrageData(data);
          
          // Always use scanTimestamp (lastUpdate from API) for display in 最后更新
          // Never use item timestamps - scanTimestamp represents when the scan was performed
          if (currentScanTimestamp) {
            try { 
              setLastUpdate(new Date(currentScanTimestamp)); 
              setCachedScanTimestamp(currentScanTimestamp); // Cache the new value
              console.log('📅 Updated lastUpdate and cached scanTimestamp:', currentScanTimestamp);
            } catch (e) {
              console.warn('⚠️ Failed to parse scanTimestamp:', currentScanTimestamp);
              setLastUpdate(null);
              setCachedScanTimestamp(null);
            }
          } else {
            // No scanTimestamp available - clear lastUpdate
            setLastUpdate(null);
            setCachedScanTimestamp(null);
            console.log('⚠️ No scanTimestamp available in API response');
          }
          setConnectionStatus('已连接');
          console.log('✅ Arbitrage data updated:', data.length, 'items');
          // Reset skip flag when data actually updates
          shouldSkipRenderRef.current = false;
          
          // Refresh history when new scan data arrives (scanTimestamp changed)
          // This happens when a new scan starts and old items are archived to history
          if (scanTimestampChanged) {
            console.log('🔄 scanTimestamp changed - refreshing history data');
            fetchHistoryData();
          }
        } else {
          // scanTimestamp unchanged - do NOT update any state to prevent UI refresh
          console.log('⏸️ scanTimestamp unchanged - skipping ALL state updates (no UI refresh)');
          // Don't update arbitrageData, lastUpdate, connectionStatus, or any other state
          // This prevents any re-renders when scanTimestamp hasn't changed
          const dataChanged = JSON.stringify(data) !== JSON.stringify(arbitrageData);
          if (dataChanged) {
            console.log('📊 Data changed detected but NOT updating (scanTimestamp unchanged)');
          } else {
            console.log('📊 No data changes detected - renderArbitrageList will be skipped');
            // Set flag to skip renderArbitrageList completely
            shouldSkipRenderRef.current = true;
          }
        }
      } else {
        console.warn('⚠️ Invalid API response format:', data);
        setArbitrageData([]);
        setConnectionStatus('数据格式错误');
        try {
          const preview = typeof data === 'string' 
            ? (data.length > 500 ? data.slice(0, 500) + '…' : data)
            : JSON.stringify(data, null, 2).slice(0, 1000);
          const typeInfo = Array.isArray(data) ? 'array' : typeof data;
          const keysInfo = data && typeof data === 'object' && !Array.isArray(data) 
            ? Object.keys(data).join(', ') 
            : '';
          window.alert(
            `数据格式错误\n\n` +
            `期望: 数组 (Array)\n` +
            `实际类型: ${typeInfo}${keysInfo ? `\n对象键: ${keysInfo}` : ''}\n` +
            `接口: ${API_BASE_URL}/api/arbitrage\n\n` +
            `返回预览:\n${preview}`
          );
        } catch (e) {
          window.alert(`数据格式错误\n(无法生成预览)\n错误: ${e.message}`);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching arbitrage data:', error);
      console.error('🔗 API URL:', API_BASE_URL);
      setConnectionStatus('连接失败');
      
      // Don't use fallback data - keep empty array on error
      setArbitrageData([]);
      console.log('💡 Keeping empty state due to API error');
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  const fetchHistoryData = async () => {
    if (!API_BASE_URL) return;
    
    try {
      setHistoryLoading(true);
      console.log('🔄 Fetching history data from:', `${API_BASE_URL}/api/arbitrage/history`);
      const response = await axios.get(`${API_BASE_URL}/api/arbitrage/history`, {
        params: { limit: 10 } // Fetch last 10 historical opportunities
      });
      const history = response.data.data || response.data || [];
      console.log('📜 History data received:', history.length, 'items');
      setHistoryData(history);
    } catch (error) {
      console.error('❌ Error fetching history data:', error);
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const triggerArbitrageScan = async () => {
    if (!API_BASE_URL) {
      console.error('❌ Cannot trigger scan: API URL not defined');
      setIsScanning(false);
      return;
    }
    
    try {
      // Disable immediately and start a client-side 5-min cooldown for instant feedback
      setIsScanning(true);
      const fiveMinutes = 5 * 60 * 1000;
      setRefreshInterval(fiveMinutes);
      setNextRefresh(new Date(Date.now() + fiveMinutes));
      console.log('🔄 Starting arbitrage scan...');
      // Immediately clear UI results in this browser instance
      setArbitrageData([]);
      setConnectionStatus('连接中...');
      setLastUpdate(null);
      setCachedScanTimestamp(null); // Clear cached scanTimestamp on manual scan
      const response = await axios.post(`${API_BASE_URL}/api/trigger-scan`, null, {
        headers: { 'x-manual-trigger': 'true' }
      });
      console.log('✅ Arbitrage scan completed');
      
      // Update countdown state from server response
      if (response.data.countdownState) {
        const serverState = response.data.countdownState;
        setNextRefresh(serverState.nextRefresh ? new Date(serverState.nextRefresh) : null);
        setRefreshInterval(serverState.refreshInterval || refreshInterval);
        setIsScanning(serverState.isScanning || false);
      }
      
      // Fetch updated data after scan
      setTimeout(() => {
        fetchArbitrageData();
        fetchCountdownState(); // Sync with server
        // History will be refreshed automatically when scanTimestamp changes (via useEffect)
      }, 2000); // Wait 2 seconds for scan to complete
      
    } catch (error) {
      console.error('❌ Error triggering arbitrage scan:', error);
      setIsScanning(false);
    }
  };

  const formatCountdown = (milliseconds) => {
    if (milliseconds <= 0) return '00:00:00';
    
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const getCountdownClass = (milliseconds) => {
    const minutes = Math.floor(milliseconds / (1000 * 60));
    if (minutes <= 1) return 'countdown urgent';
    if (minutes <= 5) return 'countdown warning';
    return 'countdown';
  };

  const getSampleData = () => [
    {
      id: "sample_1",
      timestamp: new Date().toISOString(),
      event: "2024-01-15T20:00:00Z | Premier League | Manchester United vs Liverpool",
      sport_key: "soccer_epl",
      event_id: "sample_event_1",
      profit_pct: 2.45,
      guaranteed_return: 1024.50,
      stakes: {
        "Manchester United": 450,
        "Liverpool": 350,
        "Draw": 200
      },
      best: {
        "Manchester United": ["Bet365", 2.10],
        "Liverpool": ["William Hill", 3.20],
        "Draw": ["Ladbrokes", 4.50]
      }
    },
    {
      id: "sample_2",
      timestamp: new Date().toISOString(),
      event: "2024-01-15T22:30:00Z | NBA | Lakers vs Warriors",
      sport_key: "basketball_nba",
      event_id: "sample_event_2",
      profit_pct: 1.85,
      guaranteed_return: 1018.50,
      stakes: {
        "Lakers": 600,
        "Warriors": 400
      },
      best: {
        "Lakers": ["Sportsbet", 1.85],
        "Warriors": ["Betfair", 2.15]
      }
    }
  ];

  const renderArbitrageCard = (arbitrage) => (
    <div key={arbitrage.id} className="arbitrage-card">
      <div className="arbitrage-header">
        <div className="event-info">
          <div className="event-title">{formatEventTime(arbitrage.event) || '未知赛事'}</div>
        </div>
        <div className="profit-badge">
          {arbitrage.profit_pct?.toFixed(2) || 0}% 利润
        </div>
      </div>
      
      {arbitrage.stakes && (
        <div className="stakes-section">
          <div className="stakes-title">下注分配:</div>
          {Object.entries(arbitrage.stakes).map(([outcome, amount]) => {
            const bestForOutcome = arbitrage.best && arbitrage.best[outcome];
            const platform = Array.isArray(bestForOutcome) ? bestForOutcome[0] : undefined;
            const odds = Array.isArray(bestForOutcome) ? bestForOutcome[1] : undefined;
            const deviationData = arbitrage.deviation && arbitrage.deviation[outcome];
            const deviationPct = deviationData ? deviationData.deviation_pct : null;
            
            // Determine highlight style based on deviation
            const getPlatformStyle = () => {
              if (deviationPct === null) {
                return { marginLeft: '8px', color: '#7f8c8d' };
              }
              
              // Significant positive deviation (good value) - green/yellow
              if (deviationPct > 10) {
                return {
                  marginLeft: '8px',
                  color: '#27ae60',
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(39, 174, 96, 0.15)',
                  padding: '2px 6px',
                  borderRadius: '4px'
                };
              } else if (deviationPct > 5) {
                return {
                  marginLeft: '8px',
                  color: '#f39c12',
                  fontWeight: '600',
                  backgroundColor: 'rgba(243, 156, 18, 0.15)',
                  padding: '2px 6px',
                  borderRadius: '4px'
                };
              } 
              // Significant negative deviation (poor value) - red/orange
              else if (deviationPct < -10) {
                return {
                  marginLeft: '8px',
                  color: '#e74c3c',
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(231, 76, 60, 0.15)',
                  padding: '2px 6px',
                  borderRadius: '4px'
                };
              } else if (deviationPct < -5) {
                return {
                  marginLeft: '8px',
                  color: '#e67e22',
                  fontWeight: '600',
                  backgroundColor: 'rgba(230, 126, 34, 0.15)',
                  padding: '2px 6px',
                  borderRadius: '4px'
                };
              }
              // Moderate deviation - neutral
              return { marginLeft: '8px', color: '#7f8c8d' };
            };
            
            return (
              <div key={outcome} className="stake-item">
                <span className="stake-outcome">{outcome}</span>
                {platform && odds !== undefined && (
                  <span className="stake-platform" style={getPlatformStyle()}>
                    {platform} · 赔率 {Number(odds).toFixed(2)}
                    {deviationPct !== null && (
                      <span style={{ fontSize: '0.85em', marginLeft: '4px' }}>
                        ({deviationPct > 0 ? '+' : ''}{deviationPct.toFixed(1)}%)
                      </span>
                    )}
                  </span>
                )}
                <span className="stake-amount" style={{ marginLeft: '8px' }}>${amount}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Memoize the arbitrage list to only re-render when arbitrageData or loading changes
  // Use refs to track previous values and skip computation when nothing changed
  const previousDataKeyRef = useRef(null);
  const previousLoadingRef = useRef(null);
  
  const renderArbitrageList = useMemo(() => {
    // FIRST CHECK: If flag says to skip (set when scanTimestamp unchanged and no data changes), return immediately
    if (shouldSkipRenderRef.current && cachedListRef.current) {
      shouldSkipRenderRef.current = false; // Reset flag after use
      // Return cached without any computation or logging
      return cachedListRef.current;
    }
    
    // Create a stable key from arbitrageData content
    const currentDataKey = JSON.stringify(arbitrageData);
    const dataChanged = previousDataKeyRef.current !== currentDataKey;
    const loadingChanged = previousLoadingRef.current !== loading;
    
    // SECOND CHECK: If nothing changed (data, loading, and we have cached scanTimestamp)
    // Skip recomputation entirely
    if (!dataChanged && !loadingChanged && cachedScanTimestamp && previousDataKeyRef.current !== null) {
      // Return cached output - don't recompute
      return cachedListRef.current || (
        <div className="no-data">
          <span style={{ fontSize: '48px' }}>💰</span>
          <h3>暂无套利机会</h3>
          <p>系统正在监控中，请稍后再查看</p>
        </div>
      );
    }
    
    // Actually recomputing - log why
    console.log('🔍 renderArbitrageList RECOMPUTING - arbitrageData.length:', arbitrageData.length, 'loading:', loading, 'dataChanged:', dataChanged, 'loadingChanged:', loadingChanged);
    
    let result;
    
    if (loading) {
      result = (
        <div className="loading">
          <RefreshCw className="animate-spin" size={32} />
          <p>正在加载套利机会...</p>
        </div>
      );
    } else if (arbitrageData.length === 0) {
      result = (
        <div className="no-data">
          <span style={{ fontSize: '48px' }}>💰</span>
          <h3>暂无套利机会</h3>
          <p>系统正在监控中，请稍后再查看</p>
        </div>
      );
    } else {
      // Sort by profit percentage (highest first)
      const sortedData = [...arbitrageData].sort((a, b) => (b.profit_pct || 0) - (a.profit_pct || 0));
      console.log('🔍 renderArbitrageList - sortedData.length:', sortedData.length);
      result = sortedData.map(renderArbitrageCard);
    }
    
    // Cache the result and state for next time
    cachedListRef.current = result;
    previousDataKeyRef.current = currentDataKey;
    previousLoadingRef.current = loading;
    
    return result;
  }, [arbitrageData, loading, cachedScanTimestamp]); // Dependencies: only recompute when these change

  // Show error screen if API URL is not defined
  if (!API_BASE_URL) {
    return (
      <div className="container">
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
            <Logo size={80} />
            <h1 style={{ margin: 0 }}>Easy Money</h1>
            <Logo size={80} />
          </div>
          
        </div>
        
        <div style={{
          background: 'rgba(231, 76, 60, 0.1)',
          border: '2px solid #e74c3c',
          borderRadius: '15px',
          padding: '30px',
          textAlign: 'center',
          margin: '20px 0'
        }}>
                 <h2 style={{color: '#e74c3c', marginBottom: '20px'}}>⚠️ API URL 未定义</h2>
                 <p style={{marginBottom: '20px', fontSize: '16px'}}>
                   请先启动本地服务以获取 API URL
                 </p>
                 <p style={{marginBottom: '20px', fontSize: '14px', color: '#7f8c8d'}}>
                   当前环境变量: {process.env.REACT_APP_API_URL || '未设置'}
                 </p>
          
          <div style={{
            background: '#f8f9fa',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
            textAlign: 'left'
          }}>
            <h4 style={{marginBottom: '15px'}}>📋 解决步骤:</h4>
            <ol style={{marginLeft: '20px', lineHeight: '1.6'}}>
              <li>运行: <code>./start_complete.sh --deploy</code></li>
              <li>等待 Cloudflare Tunnel URL 生成</li>
              <li>刷新此页面</li>
            </ol>
          </div>
          
          <div style={{
            background: '#e8f5e8',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <p style={{margin: 0, fontWeight: 'bold'}}>
              💡 提示: 应用会自动检测并更新 API URL
            </p>
          </div>
          
          <button
            onClick={() => window.location.reload()}
            style={{
              background: '#3498db',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            🔄 刷新页面
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
          <Logo size={80} />
          <h1 style={{ margin: 0 }}>Easy Money</h1>
          <Logo size={80} />
        </div>
        
      </div>

      <div className="status-bar">
        <div className="status-item">
          <div className={`status-dot ${apiUrlStatus === 'connected' ? 'connected' : apiUrlStatus === 'error' ? 'error' : 'checking'}`}></div>
          <span>
            {isScanning ? '扫描中...' : connectionStatus}
            {apiUrlStatus === 'error' && (
              <span style={{color: '#e74c3c', marginLeft: '8px'}}>
                (API连接失败 - 请检查Cloudflare Tunnel)
                <button 
                  onClick={() => setShowUrlInput(!showUrlInput)}
                  style={{
                    marginLeft: '8px',
                    padding: '2px 8px',
                    fontSize: '12px',
                    background: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {showUrlInput ? '取消' : '更新URL'}
                </button>
              </span>
            )}
          </span>
        </div>
        <div className="status-item">
          <Clock size={16} />
          <span>最后更新: {lastUpdate ? `${formatBrisbaneTime(lastUpdate)} (Brisbane)` : '--'}</span>
        </div>
        <div className="status-item">
          <Trophy size={16} />
          <span>机会数: {arbitrageData.length}</span>
        </div>
        <div className="status-item">
          <span>👥 在线: {viewerCount}</span>
        </div>
        <div className="status-item">
          <button 
            className="refresh-btn" 
            onClick={triggerArbitrageScan}
            disabled={isScanning || (nextRefresh && Date.now() < nextRefresh.getTime())}
            style={{ 
              opacity: (isScanning || (nextRefresh && Date.now() < nextRefresh.getTime())) ? 0.6 : 1,
              cursor: (isScanning || (nextRefresh && Date.now() < nextRefresh.getTime())) ? 'not-allowed' : 'pointer',
              marginRight: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {isScanning && <RefreshCw className="animate-spin" size={14} />}
            {isScanning ? '扫描中...' : (nextRefresh && Date.now() < nextRefresh.getTime()) ? '冷却中' : '手动扫描'}
          </button>
        </div>
        {(() => {
          const active = !RUBY_MONITORING && nextRefresh && Date.now() < nextRefresh.getTime();
          if (!active && !isScanning) return null;
          return (
        <div className={`status-item ${countdown <= 60000 ? 'urgent' : countdown <= 300000 ? 'warning' : ''}`}>
          <span>
            {isScanning && <RefreshCw className="animate-spin" size={14} style={{marginRight:'6px'}}/>}
            {!RUBY_MONITORING && '⏳ 冷却:'}
            {!RUBY_MONITORING && <span className={getCountdownClass(countdown)}> {formatCountdown(countdown)}</span>}
          </span>
          {!RUBY_MONITORING && (
            <div className="countdown-progress">
              <div 
                className="countdown-bar" 
                style={{ 
                  width: `${Math.max(0, Math.min(100, (countdown / refreshInterval) * 100))}%` 
                }}
              ></div>
            </div>
          )}
        </div>
          );
        })()}
      </div>

      {showUrlInput && (
        <div style={{
          background: 'rgba(231, 76, 60, 0.1)',
          border: '1px solid #e74c3c',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          <h4 style={{color: '#e74c3c', marginBottom: '10px'}}>更新 API URL</h4>
          <p style={{marginBottom: '10px', fontSize: '14px'}}>
            当前URL: <code style={{background: '#f8f9fa', padding: '2px 6px', borderRadius: '4px'}}>{API_BASE_URL}</code>
          </p>
          <div style={{display: 'flex', gap: '10px', justifyContent: 'center', alignItems: 'center'}}>
            <input
              type="text"
              placeholder="https://your-url.trycloudflare.com"
              value={manualUrl}
              onChange={(e) => setManualUrl(e.target.value)}
              style={{
                flex: 1,
                maxWidth: '400px',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
            <button
              onClick={() => updateApiUrl(manualUrl)}
              disabled={!manualUrl.startsWith('http')}
              style={{
                padding: '8px 16px',
                background: manualUrl.startsWith('http') ? '#27ae60' : '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: manualUrl.startsWith('http') ? 'pointer' : 'not-allowed',
                fontSize: '14px'
              }}
            >
              更新
            </button>
            <button
              onClick={() => {
                setShowUrlInput(false);
                setManualUrl('');
              }}
              style={{
                padding: '8px 16px',
                background: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              取消
            </button>
          </div>
          <p style={{marginTop: '10px', fontSize: '12px', color: '#7f8c8d'}}>
            💡 提示: 从Cloudflare Tunnel输出中复制完整的HTTPS URL
          </p>
        </div>
      )}

      <div className="main-content">
        <div ref={arbitrageSectionRef} className="arbitrage-section">
          <h2 className="section-title">
            <span style={{ fontSize: '24px', marginRight: '8px' }}>💰</span>
            套利机会
          </h2>
          <div 
            ref={arbitrageListRef}
            style={{ 
              overflowY: 'auto', 
              minHeight: 0,
              maxHeight: arbitrageListHeight ? `${arbitrageListHeight}px` : 'none',
              height: arbitrageListHeight ? `${arbitrageListHeight}px` : 'auto'
            }}
          >
            {renderArbitrageList}
          </div>
        </div>

        <div className="sidebar">
          <div className="sidebar-card">
            <h3 className="section-title">
              <History size={20} />
              历史机会
            </h3>
            {historyLoading ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#7f8c8d' }}>
                <RefreshCw className="animate-spin" size={20} style={{ margin: '0 auto' }} />
                <p style={{ marginTop: '10px', fontSize: '14px' }}>加载中...</p>
              </div>
            ) : historyData.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#7f8c8d' }}>
                <p style={{ fontSize: '14px' }}>暂无历史记录</p>
              </div>
            ) : (
              <div 
                ref={historyContainerRef}
                style={{ 
                  overflowY: 'auto', 
                  minHeight: 0,
                  maxHeight: historyMaxHeight ? `${historyMaxHeight}px` : 'none',
                  height: historyMaxHeight ? `${historyMaxHeight}px` : 'auto'
                }}
              >
                {[...historyData].sort((a, b) => (b.profit_pct || 0) - (a.profit_pct || 0)).slice(0, 10).map((item, index) => (
                  <div 
                    key={item.id || index} 
                    className="arbitrage-card"
                    style={{
                      padding: '20px',
                      marginBottom: '15px'
                    }}
                  >
                    <div className="arbitrage-header">
                      <div className="event-info">
                        <div className="event-title" style={{ color: '#2c3e50' }}>
                          {item.event ? formatEventTime(item.event) : '未知赛事'}
                        </div>
                      </div>
                      <div className="profit-badge">
                        {item.profit_pct?.toFixed(2) || 0}% 利润
                      </div>
                    </div>
                    
                    {item.stakes && (
                      <div className="stakes-section">
                        <div className="stakes-title" style={{ color: '#34495e' }}>下注分配:</div>
                        {Object.entries(item.stakes).map(([outcome, amount]) => {
                          const bestForOutcome = item.best && item.best[outcome];
                          const platform = Array.isArray(bestForOutcome) ? bestForOutcome[0] : undefined;
                          const odds = Array.isArray(bestForOutcome) ? bestForOutcome[1] : undefined;
                          
                          return (
                            <div key={outcome} className="stake-item">
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span className="stake-outcome" style={{ color: '#2c3e50' }}>{outcome}</span>
                                {platform && odds !== undefined && (
                                  <span style={{ fontSize: '0.9rem', color: '#34495e' }}>
                                    {platform} · {Number(odds).toFixed(2)}
                                  </span>
                                )}
                              </div>
                              <span className="stake-amount" style={{ color: '#388E3C' }}>${amount}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {item.archivedAt && (
                      <div style={{ fontSize: '0.85rem', color: '#7f8c8d', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e9ecef' }}>
                        {new Date(item.archivedAt).toLocaleString('zh-CN', { 
                          month: 'short', 
                          day: 'numeric', 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ 
        background: 'rgba(255, 255, 255, 0.05)', 
        borderRadius: '15px', 
        padding: '30px', 
        marginTop: '30px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}>
          <div>
            <h3 className="section-title" style={{ marginBottom: '15px', fontSize: '18px' }}>
              <MessageCircle size={20} />
              讨论群组
            </h3>
            <div className="chat-links" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <a
                className="chat-link"
                style={{ color: '#95a5a6', cursor: 'not-allowed', pointerEvents: 'none' }}
                title="即将上线"
              >
                📱 Telegram 群组
              </a>
              <a href="https://discordapp.com/channels/1433596333528449157/1433596334719504420" className="chat-link" target="_blank" rel="noopener noreferrer">
                💬 Discord 频道
              </a>
              <a
                className="chat-link"
                style={{ color: '#95a5a6', cursor: 'not-allowed', pointerEvents: 'none' }}
                title="即将上线"
              >
                📞 WhatsApp 群组
              </a>
            </div>
          </div>

          <div>
            <h3 className="section-title" style={{ marginBottom: '15px', fontSize: '18px' }}>
              <Megaphone size={20} />
              广告位
            </h3>
            <div className="ad-block" style={{ marginBottom: '15px' }}>
              <h4>广告位 1</h4>
              <p>300x250 像素</p>
            </div>
            <div className="ad-block">
              <h4>广告位 2</h4>
              <p>300x250 像素</p>
            </div>
          </div>
        </div>
      </div>

      <footer className="site-footer">
        <p>
          本网站所展示的内容与数据仅用于学习与研究目的，不构成任何投资、博彩或交易建议。
          使用者应自行承担使用本网站信息所产生的全部风险与责任。
        </p>
      </footer>
    </div>
  );
}

export default App;
